/**
 * Letture trasversali sui set, per il guscio della dashboard (ADR 0030 §2):
 * la ricerca dei set del selettore e i conteggi «da fare» di notifiche,
 * Panoramica ed elenco dei set.
 *
 * - `GET /api/v1/agent-sets/search` esiste dal 2026-10 (ADR 0018 §5) e la usa
 *   l'app mobile: `{items, total}`, per nome, senza i set archiviati.
 * - `GET /api/v1/agent-sets/attention` è nuova (ADR 0030 §6). Finché il backend
 *   non la pubblica risponde 404, e chi la usa nasconde ciò che ne dipende.
 *   La risposta si normalizza con `normalizeAttention`, che accetta sia
 *   `{items: [...]}` sia un elenco nudo e i nomi di campo più probabili: il
 *   contratto è dichiarato qui sotto, e le varianti non rompono la pagina.
 *
 * - `GET /api/v1/agent-sets/usage/daily` (e `.csv`) e `GET /api/v1/agent-sets/messages`
 *   (e `.csv`): consumo per giorno della Panoramica e cronologia unica (tappe 6 e 8).
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano.
 */

import type { AgentSetStatus } from './agentTypes';
import type { ApiClient } from './client';
import { historyQuery, type DailyUsage, type HistoryFilters, type HistoryPage } from './overviewTypes';

/** Un set nella ricerca. */
export interface SetSearchItem {
  /** Identificativo. */
  id: string;
  /** Nome. */
  name: string;
  /** Identificativo leggibile. */
  slug: string;
  /** Stato del ciclo di vita (mai `archived`). */
  status: AgentSetStatus;
  /** Progetti del set. */
  projects_count: number;
}

/** Una pagina della ricerca dei set. */
export interface SetSearchPage {
  /** Set della pagina, per nome. */
  items: SetSearchItem[];
  /** Set che corrispondono, in tutte le pagine. */
  total: number;
}

/** Ciò che chiede un intervento in un set. */
export interface SetAttention {
  /** Set. */
  set_id: string;
  /** Nome del set; vuoto se il backend non lo manda. */
  set_name: string;
  /** Proposte di task da approvare. */
  proposals: number;
  /** Analisi di progetti affidati da approvare (ADR 0024). */
  analyses: number;
  /** True se il runtime è in errore. */
  runtime_failed: boolean;
  /** True se manca la credenziale di Claude. */
  credential_missing: boolean;
  /** True se il set è in pausa per il limite del piano. */
  paused_for_limit: boolean;
}

/**
 * Legge un numero da un oggetto, provando più nomi di campo.
 *
 * @param raw Oggetto ricevuto.
 * @param names Nomi possibili, in ordine di preferenza.
 * @returns Il primo numero trovato, 0 se nessuno.
 */
function numberOf(raw: Record<string, unknown>, names: readonly string[]): number {
  for (const name of names) {
    const value = raw[name];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

/**
 * Legge un vero/falso da un oggetto, provando più nomi di campo.
 *
 * @param raw Oggetto ricevuto.
 * @param names Nomi possibili.
 * @returns True se uno dei campi è vero o un numero positivo.
 */
function flagOf(raw: Record<string, unknown>, names: readonly string[]): boolean {
  return names.some((name) => raw[name] === true || (typeof raw[name] === 'number' && (raw[name] as number) > 0));
}

/**
 * Riduce la risposta di `attention` alla forma che la dashboard usa.
 *
 * @param body Corpo ricevuto.
 * @returns Una voce per set; le voci senza identificativo si scartano.
 */
export function normalizeAttention(body: unknown): SetAttention[] {
  const list: unknown = Array.isArray(body)
    ? body
    : typeof body === 'object' && body !== null
      ? ((body as Record<string, unknown>).items ?? (body as Record<string, unknown>).sets)
      : null;
  if (!Array.isArray(list)) {
    return [];
  }
  const result: SetAttention[] = [];
  for (const entry of list) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const raw = entry as Record<string, unknown>;
    const id = raw.set_id ?? raw.agent_set_id ?? raw.id;
    if (typeof id !== 'string' || id === '') {
      continue;
    }
    const name = raw.set_name ?? raw.name;
    result.push({
      set_id: id,
      set_name: typeof name === 'string' ? name : '',
      proposals: numberOf(raw, ['proposals', 'pending_proposals', 'task_proposals']),
      analyses: numberOf(raw, ['analyses', 'pending_analyses', 'analyses_pending']),
      runtime_failed: flagOf(raw, ['runtime_failed', 'failed', 'runtime_error']),
      credential_missing: flagOf(raw, ['credential_missing', 'missing_credential', 'credentials_missing']),
      paused_for_limit: flagOf(raw, ['paused_for_limit', 'paused', 'limit_paused']),
    });
  }
  return result;
}

/**
 * Quante cose chiedono un intervento in un set.
 *
 * @param item Voce di un set.
 * @returns Il totale: proposte, analisi e una per ogni avviso acceso.
 */
export function attentionTotal(item: SetAttention): number {
  return (
    item.proposals +
    item.analyses +
    (item.runtime_failed ? 1 : 0) +
    (item.credential_missing ? 1 : 0) +
    (item.paused_for_limit ? 1 : 0)
  );
}

/** Letture trasversali sui set. */
export class DirectoryApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Cerca fra i set non archiviati del chiamante (tutti, per un `ADMIN`).
   *
   * @param q Testo cercato in nome e slug.
   * @param limit Set della pagina.
   * @param offset Set da saltare.
   * @returns La pagina.
   */
  searchSets(q: string, limit = 30, offset = 0): Promise<SetSearchPage> {
    const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
    return this.client.request<SetSearchPage>('GET', `/api/v1/agent-sets/search?${params.toString()}`);
  }

  /**
   * Legge ciò che chiede un intervento, set per set.
   *
   * @returns Le voci normalizzate.
   * @throws ApiError 404 finché il backend non pubblica la rotta.
   */
  async getAttention(): Promise<SetAttention[]> {
    return normalizeAttention(await this.client.request<unknown>('GET', '/api/v1/agent-sets/attention'));
  }

  /**
   * Token conteggiati per giorno UTC, con il dettaglio per set.
   *
   * @param days Giorni, da 7 a 90, oggi compreso.
   * @param setId Solo questo set; omesso, i set del chiamante.
   * @param byProject Con un set, anche i token per progetto di ogni giorno (ADR 0032).
   * @returns I giorni, il totale, il giorno di picco e il prezzo indicativo.
   */
  getDailyUsage(days: number, setId?: string, byProject = false): Promise<DailyUsage> {
    const query = dailyQuery(days, setId);
    const extra = byProject && setId !== undefined ? '&by_project=true' : '';
    return this.client.request<DailyUsage>('GET', `/api/v1/agent-sets/usage/daily?${query}${extra}`);
  }

  /**
   * Il consumo per giorno in CSV: una riga per giorno, una colonna per set.
   *
   * @param days Giorni, da 7 a 90.
   * @param setId Solo questo set.
   * @returns Il file.
   */
  exportDailyUsage(days: number, setId?: string): Promise<Blob> {
    return this.client.requestBlob(`/api/v1/agent-sets/usage/daily.csv?${dailyQuery(days, setId)}`);
  }

  /**
   * Una pagina della cronologia unica (al più 50 voci, dalla più recente).
   *
   * @param filters Filtri e pagina.
   * @returns La pagina, con il totale.
   */
  getHistory(filters: HistoryFilters): Promise<HistoryPage> {
    const query = historyQuery(filters);
    return this.client.request<HistoryPage>('GET', `/api/v1/agent-sets/messages${query === '' ? '' : `?${query}`}`);
  }

  /**
   * La cronologia con gli stessi filtri in CSV (al più 5000 righe).
   *
   * @param filters Filtri; pagina e limite si ignorano.
   * @returns Il file.
   */
  exportHistory(filters: HistoryFilters): Promise<Blob> {
    const query = historyQuery({ ...filters, limit: undefined, offset: undefined });
    return this.client.requestBlob(`/api/v1/agent-sets/messages.csv${query === '' ? '' : `?${query}`}`);
  }
}

/**
 * Query del consumo per giorno.
 *
 * @param days Giorni.
 * @param setId Set, facoltativo.
 * @returns La query, senza `?`.
 */
function dailyQuery(days: number, setId?: string): string {
  const params = new URLSearchParams({ days: String(days) });
  if (setId !== undefined) {
    params.set('set_id', setId);
  }
  return params.toString();
}
